import { getColorDistance, normalizeHexColor } from './colorUtils';

export type MatchQuality = 'exact' | 'color' | 'type' | 'none';

export interface SlotCandidate {
  slot: number;
  type: string;
  color: string;
  filamentId?: string;
  extruderId?: number;
}

export interface MatchFilament {
  type: string;
  color: string;
  nozzle?: number;
}

export interface SlotMatchResult {
  slot: number;
  matchQuality: MatchQuality;
  colorDistance?: number;
}

export interface MatchOptions {
  colorThreshold?: number;
  allowReuseSlots?: boolean;
}

const DEFAULT_COLOR_THRESHOLD = 40;

export function findBestSlotMatch(
  filament: MatchFilament,
  slots: SlotCandidate[],
  usedSlots?: Set<number>,
  options?: MatchOptions
): SlotMatchResult {
  const threshold = options?.colorThreshold ?? DEFAULT_COLOR_THRESHOLD;
  const used = usedSlots ?? new Set<number>();

  const available = slots.filter(s => !used.has(s.slot));

  if (available.length === 0) {
    return { slot: -1, matchQuality: 'none' };
  }

  const filamentColor = normalizeHexColor(filament.color) ?? '';
  const filamentType = filament.type.toUpperCase();

  // 1. Exact match: same type + same normalized color
  for (const s of available) {
    const slotColor = normalizeHexColor(s.color) ?? '';
    const slotType = s.type.toUpperCase();
    if (slotType === filamentType && slotColor === filamentColor) {
      return { slot: s.slot, matchQuality: 'exact' };
    }
  }

  // 2. Color match: same type, color within threshold
  const sameType = available.filter(s => s.type.toUpperCase() === filamentType);
  if (sameType.length > 0) {
    let bestSlot = -1;
    let bestDist = Infinity;
    for (const s of sameType) {
      const dist = getColorDistance(filament.color, s.color);
      if (dist < bestDist) {
        bestDist = dist;
        bestSlot = s.slot;
      }
    }
    if (bestDist <= threshold) {
      return { slot: bestSlot, matchQuality: 'color', colorDistance: bestDist };
    }
  }

  // 3. Type match: same type, any color
  if (sameType.length > 0) {
    return { slot: sameType[0].slot, matchQuality: 'type' };
  }

  // 4. No match
  return { slot: -1, matchQuality: 'none' };
}

export function matchAllFilaments(
  filaments: MatchFilament[],
  slots: SlotCandidate[],
  options?: MatchOptions
): SlotMatchResult[] {
  const used = new Set<number>();
  const results: SlotMatchResult[] = [];

  for (const filament of filaments) {
    const result = findBestSlotMatch(
      filament,
      slots,
      options?.allowReuseSlots ? undefined : used,
      options
    );
    results.push(result);
    if (result.slot >= 0 && !options?.allowReuseSlots) {
      used.add(result.slot);
    }
  }

  return results;
}
